import React, { useLayoutEffect, useEffect, useRef } from 'react';
import videojs from 'video.js';
import type { VideoJsPlayer } from 'video.js';

export interface VideoJSPlayerProps
  extends Omit<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    'src' | 'children' | 'onTimeUpdate'
  > {
  /**
  * источник (blob или URL)
  */
  src: string;
  /**
  * превью до старта (Video.js использует prop poster)
  */
  poster?: string;
  /**
  * класс для контейнера с плеером
  */
  className?: string;
  /**
  * управляемый плей/пауза
  */
  playing?: boolean;
  /**
  * управляемая перемотка видео
  * установка текущего времени воспроизведения в секундах
  */
  currentTime?: number;
  /**
  * обратное событие при изменении состояния плей/пауза в плеере
  */
  onPlayingChange?: (v: boolean) => void;
  /**
  * репортит текущее время воспроизведения
  */
  onTimeUpdateSec?: (t: number) => void;
  /**
  * отдает ширину/высоту/длительность видео
  * один раз после метаданных (для масштаба боксов)
  */
  onLoaded?: (m: { videoWidth: number; videoHeight: number; duration: number }) => void;
}

export function VideoJSPlayer({
  src,
  poster = undefined,
  className = undefined,
  playing = false,
  currentTime = 0,
  onPlayingChange = undefined,
  onTimeUpdateSec = undefined,
  onLoaded = undefined,
  ...attrs }: VideoJSPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null); // ссылка на DOM
  const playerRef = useRef<VideoJsPlayer | null>(null); // ссылка на video.js wrapper
  const lastSetTimeRef = useRef<number | null>(null); // чтобы избежать петель

  // отложенные действия, если источник ещё не готов
  const pendingSeekRef = useRef<number | null>(null);
  const pendingPlayRef = useRef<boolean>(false);

  // общая функция применения источника (используем и в init, и при смене src)
  const applySource = (source: string) => {
    const node = videoRef.current!;
    const currentPlayer = playerRef.current!;
    // запомним желаемые действия на случай, если метаданные ещё не готовы
    pendingPlayRef.current = !!playing;
    pendingSeekRef.current = typeof currentTime === 'number' ? currentTime : null;

    if (!source) {
      currentPlayer.pause();
      try { currentPlayer.src([] as any); } catch { }
      node.removeAttribute('src');
      node.load();
      return;
    }

    if (source.startsWith('blob:')) {
      // навешиваем одноразовый loadedmetadata ДО load()
      const onMetaOnce = () => {
        onLoaded?.({
          videoWidth: node.videoWidth,
          videoHeight: node.videoHeight,
          duration: currentPlayer.duration() ?? node.duration ?? 0,
        });
        if (pendingSeekRef.current != null) {
          currentPlayer.currentTime(pendingSeekRef.current);
          pendingSeekRef.current = null;
        }
        if (pendingPlayRef.current) {
          const playResult = currentPlayer.play?.();
          if (playResult && typeof (playResult as Promise<void>).catch === 'function') {
            (playResult as Promise<void>).catch(() => { });
          }
          pendingPlayRef.current = false;
        }
        node.removeEventListener('loadedmetadata', onMetaOnce);
      };
      node.addEventListener('loadedmetadata', onMetaOnce, { once: true });

      // сбрасываем источники video.js и ставим blob напрямую
      currentPlayer.pause();
      try { currentPlayer.src([] as any); } catch { }
      node.src = source;
      node.load();
      return;
    }

    if (source.toLowerCase().includes('.m3u8')) {
      currentPlayer.src({ src: source, type: 'application/x-mpegURL' });
      return;
    }

    currentPlayer.src({ src: source }); // обычный URL
  };

  // INIT (и сразу применяем текущий src)
  useLayoutEffect(() => {
    const node = videoRef.current;
    if (!node || playerRef.current) return;

    let alive = true;
    let rafId = 0; // requestAnimationFrame ID для возможной отмены

    const init = () => {
      if (!alive) return;
      const connected = (node as any).isConnected || document.body.contains(node);
      if (!connected) { rafId = requestAnimationFrame(init); return; }

      const player = videojs(node, {
        controls: true,
        preload: 'metadata',
        fill: true,
        controlBar: {
          pictureInPictureToggle: true,
          remainingTimeDisplay: true,
          volumePanel: { inline: false },
        },
        poster,
      });
      playerRef.current = player;

      const onLoadedMeta = () => {
        onLoaded?.({
          videoWidth: node.videoWidth,
          videoHeight: node.videoHeight,
          duration: player.duration() ?? node.duration ?? 0,
        });
      };

      const onTimeUpdateHandler = () => {
        const currentTimeInPlayer = player.currentTime();
        if (typeof currentTimeInPlayer !== 'number') return;
        if (lastSetTimeRef.current != null && Math.abs(currentTimeInPlayer - lastSetTimeRef.current) < 0.02) return;
        onTimeUpdateSec?.(currentTimeInPlayer);
      };

      const handlePlay = () => onPlayingChange?.(true);
      const handlePause = () => onPlayingChange?.(false);
      const handleEnded = () => onPlayingChange?.(false);

      player.on('loadedmetadata', onLoadedMeta);
      player.on('canplay', onLoadedMeta);
      player.on('timeupdate', onTimeUpdateHandler);
      player.on('play', handlePlay);
      player.on('pause', handlePause);
      player.on('ended', handleEnded);

      applySource(src);

      (player as any)._cleanup = () => {
        player.off('loadedmetadata', onLoadedMeta);
        player.off('canplay', onLoadedMeta);
        player.off('timeupdate', onTimeUpdateHandler);
        player.off('play', handlePlay);
        player.off('pause', handlePause);
        player.off('ended', handleEnded);
        if (!player.isDisposed()) player.dispose();
        playerRef.current = null;
      };
    };

    rafId = requestAnimationFrame(init);
    return () => {
      alive = false;
      if (rafId) cancelAnimationFrame(rafId);
      const currentPlayer = playerRef.current as any;
      if (currentPlayer?._cleanup) currentPlayer._cleanup();
    };
  }, []);

  // при смене src (когда player уже есть) — просто переустанавливаем источник
  useEffect(() => {
    if (!videoRef.current || !playerRef.current) return;
    applySource(src);
  }, [src]);

  // контролируем play/pause (если источник уже готов)
  useEffect(() => {
    const currentPlayer = playerRef.current;
    if (!currentPlayer || typeof playing !== 'boolean') return;
    if (playing) {
      const playResult = currentPlayer.play?.();
      if (playResult && typeof (playResult as Promise<void>).catch === 'function') {
        (playResult as Promise<void>).catch(() => { });
      }
    } else {
      currentPlayer.pause?.();
    }
  }, [playing]);

  // контролируем seek
  useEffect(() => {
    const currentPlayer = playerRef.current;
    if (!currentPlayer || typeof currentTime !== 'number') return;
    const currentTimeInCurrentPlayer = currentPlayer.currentTime();
    if (Math.abs(currentTimeInCurrentPlayer - currentTime) > 0.02) {
      lastSetTimeRef.current = currentTime;
      if (currentPlayer.readyState() >= 1) {
        currentPlayer.currentTime(currentTime);
        queueMicrotask(() => { lastSetTimeRef.current = null; });
      } else {
        pendingSeekRef.current = currentTime;
      }
    }
  }, [currentTime]);

  return (
    <div data-vjs-player className={className}>
      <video
        ref={videoRef}
        className="video-js vjs-default-skin"
        playsInline
        {...attrs}
      />
    </div>
  );
};
